import { useState, useCallback, type TouchEvent, type MouseEvent } from 'react';

type UseTouchClickOptions = {
  onClick?: (e: MouseEvent) => void;
  onTouchStart?: (e: TouchEvent<HTMLElement>) => void;
  onTouchMove?: (e: TouchEvent<HTMLElement>) => void;
  onTouchEnd?: (e: TouchEvent<HTMLElement>) => void;
};

type UseTouchClickReturn = {
  handleTouchStart: (e: TouchEvent<HTMLElement>) => void;
  handleTouchMove: (e: TouchEvent<HTMLElement>) => void;
  handleTouchEnd: (e: TouchEvent<HTMLElement>) => void;
};

/**
 * A custom React hook that provides touch and click event handling logic to prevent double invocation of `onClick`.
 * This hook ensures that touch interactions (e.g., on mobile devices) do not trigger duplicate `onClick` events.
 *
 * @example
 * const MyButton = ({ onClick, ...props }) => {
 *   const { handleTouchStart, handleTouchMove, handleTouchEnd } = useTouchClick({ onClick });
 *   return <button
 *      {...props}
 *      onClick={onClick}
 *      onTouchStart={handleTouchStart}
 *      onTouchMove={handleTouchMove}
 *      onTouchEnd={handleTouchEnd}
 *    >
 *      Click Me
 *    </button>;
 * };
 *
 * // Usage
 * <MyButton onClick={() => console.log('Clicked')} />
 */
export const useTouchClick = ({
  onClick,
  onTouchStart,
  onTouchMove,
  onTouchEnd,
}: UseTouchClickOptions): UseTouchClickReturn => {
  const [shouldHandleTap, setShouldHandleTap] = useState(false);

  const handleTouchStart = useCallback(
    (event: TouchEvent<HTMLElement>) => {
      if (onClick) {
        setShouldHandleTap(true);
      }
      onTouchStart?.(event);
    },
    [onTouchStart],
  );

  const handleTouchMove = useCallback(
    (event: TouchEvent<HTMLElement>) => {
      if (onClick) {
        setShouldHandleTap(false);
      }
      onTouchMove?.(event);
    },
    [onTouchMove],
  );

  const handleTouchEnd = useCallback(
    (event: TouchEvent<HTMLElement>) => {
      onTouchEnd?.(event);

      if (shouldHandleTap) {
        event.preventDefault();
        event.currentTarget.blur();
        setShouldHandleTap(false);
        onClick?.(event as unknown as MouseEvent);
      }
    },
    [shouldHandleTap, onClick, onTouchEnd],
  );

  return {
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
  };
};
